export const CONSENT = {
    NOT_SET: 'NOT_SET',
    OPT_IN: 'OPT_IN',
    OPT_OUT: 'OPT_OUT'
}

export const CategoryIndex = {
    // Default category IDs; runtime logic auto-detects one-based vs zero-based.
    required: 0,
    functional: 1,
    advertising: 2,
    multimedia: 3
}

const CategoryIndexOneBased = {
    required: 1,
    functional: 2,
    advertising: 3,
    multimedia: 4
}

function getCookieValue(cookieName) {
    if (typeof document === 'undefined') {
        return null
    }

    const encodedCookieName = `${cookieName}=`
    const cookies = document.cookie ? document.cookie.split(';') : []

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim()
        if (cookie.indexOf(encodedCookieName) === 0) {
            return decodeURIComponent(cookie.substring(encodedCookieName.length))
        }
    }

    return null
}

function getTrustArcApi() {
    if (typeof window === 'undefined') {
        return null
    }

    const truste = window.truste
    if (!truste || !truste.cma || typeof truste.cma.callApi !== 'function') {
        return null
    }

    return truste.cma.callApi
}

function getRawGDPRConsentDecision() {
    const callApi = getTrustArcApi()
    if (!callApi || typeof window === 'undefined') {
        return {
            source: null,
            consentDecision: [],
            callApi
        }
    }

    const result = callApi('getGDPRConsentDecision', window.location.hostname)
    const consentDecision = Array.isArray(result && result.consentDecision)
        ? result.consentDecision
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value))
        : []

    return {
        source: result && result.source ? result.source : null,
        consentDecision,
        callApi
    }
}

function resolveCategoryIndexMap(consentDecision) {
    const hasZero = consentDecision.includes(0)
    const hasPositive = consentDecision.some((value) => value > 0)

    if (!hasZero && hasPositive) {
        return CategoryIndexOneBased
    }

    return CategoryIndex
}

export function getConsentCategories() {
    const defaultConsent = {
        required: CONSENT.NOT_SET,
        functional: CONSENT.NOT_SET,
        advertising: CONSENT.NOT_SET,
        multimedia: CONSENT.NOT_SET
    }

    if (typeof window === 'undefined') {
        return defaultConsent
    }

    const { source, consentDecision } = getRawGDPRConsentDecision()
    if (!consentDecision.length && source === null) {
        return defaultConsent
    }

    if (!consentDecision.length || (consentDecision.length === 1 && consentDecision[0] === 0)) {
        return defaultConsent
    }

    const categoryIndexMap = resolveCategoryIndexMap(consentDecision)

    const determineConsent = (categoryName) => {
        const hasConsent = consentDecision.includes(categoryIndexMap[categoryName])

        if (source === 'asserted') {
            return hasConsent ? CONSENT.OPT_IN : CONSENT.OPT_OUT
        }

        return hasConsent ? CONSENT.OPT_IN : CONSENT.NOT_SET
    }

    return {
        required: determineConsent('required'),
        functional: determineConsent('functional'),
        advertising: determineConsent('advertising'),
        multimedia: determineConsent('multimedia')
    }
}

export function getRegionValidationConsent(consent) {
    const behaviour = getCookieValue('notice_behavior')

    if (consent === CONSENT.OPT_IN) {
        return true
    }

    if (behaviour && behaviour.includes('eu')) {
        return false
    }

    if (behaviour && behaviour.includes('us') && consent === CONSENT.OPT_OUT) {
        return false
    }

    return true
}

export function hasMultimediaConsent(options = {}) {
    const multimediaConsentEnabled = options.multimediaConsentEnabled !== false
    const consentCategories = getConsentCategories()
    const consentToValidate = multimediaConsentEnabled ? consentCategories.multimedia : consentCategories.advertising

    return getRegionValidationConsent(consentToValidate)
}

export function shouldPromptMultimediaPlayerChoice(options = {}) {
    const multimediaConsentEnabled = options.multimediaConsentEnabled !== false
    const consentCategories = getConsentCategories()
    const consentToValidate = multimediaConsentEnabled ? consentCategories.multimedia : consentCategories.advertising
    const behaviour = (getCookieValue('notice_behavior') || '').toLowerCase()

    const isOptedOut = consentToValidate === CONSENT.OPT_OUT
    const isDefaultOptInRegion = behaviour.includes('us') && consentToValidate === CONSENT.NOT_SET

    return isOptedOut || isDefaultOptInRegion
}

export function setTrustArcConsentLevel(level) {
    if (typeof window === 'undefined') {
        return false
    }

    if (!['functional', 'advertising', 'multimedia'].includes(level)) {
        return false
    }

    const { consentDecision, callApi } = getRawGDPRConsentDecision()
    if (!callApi) {
        return false
    }

    try {
        const currentConsent = getConsentCategories()
        const categoryIndexMap = resolveCategoryIndexMap(consentDecision)
        const consentLevels = [categoryIndexMap.required]

        for (const cat of ['functional', 'advertising', 'multimedia']) {
            if (level === cat || currentConsent[cat] === CONSENT.OPT_IN) {
                consentLevels.push(categoryIndexMap[cat])
            }
        }

        callApi('setConsentLevels', window.location.hostname, consentLevels)
        window.dispatchEvent(new Event('multimediaConsentChanged'))

        return true
    } catch (error) {
        console.warn('Failed to set TrustArc consent levels:', error)
        return false
    }
}

export function openTrustArcPreferences() {
    const consentButton = document.querySelector('#consent_blackbar #truste-show-consent')
    if (consentButton && typeof consentButton.click === 'function') {
        consentButton.click()
        return
    }

    const truste = window.truste
    if (!truste || !truste.eu || typeof truste.eu.clickListener !== 'function') {
        return
    }

    const bindMap = truste.eu.bindMap || {}
    if (truste.bn && typeof truste.bn.msglog === 'function' && bindMap.bannerMsgURL) {
        truste.bn.msglog('moreinfo', bindMap.bannerMsgURL)
    }

    const isIabBanner = !!(bindMap.feat && bindMap.feat.iabBannerApplies)
    const pn = Number.parseInt(bindMap.params && bindMap.params.pn, 10) || 0

    truste.eu.clickListener(isIabBanner ? 4 : pn)
}
